import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  username: string;
  email: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
  isAdmin?: boolean;
  role?: string;
  hasSeenWelcomeTour?: boolean;
  defaultPublicShares?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  setAuth: (user: User, token: string) => void;
  updateUser: (user: User) => void;
  clearAuth: () => void;
  isAuthenticated: boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => set({ user, token, isAuthenticated: true }),
      updateUser: (userData) => set((state) => ({
        user: state.user ? { ...state.user, ...userData } : userData
      })),
      clearAuth: () => set({ user: null, token: null, isAuthenticated: false }),
    }),
    {
      name: 'ourss-auth',
    }
  )
);

interface Article {
  id: string;
  title: string;
  excerpt?: string;
  content: string;
  url: string;
  imageUrl?: string;
  publishedAt?: string;
  isRead: boolean;
  isLiked: boolean;
  feed: {
    id: string;
    title: string;
    siteUrl?: string;
  };
  _count: {
    comments: number;
    shares: number;
    likes: number;
  };
}

interface ArticlesState {
  articles: Article[];
  selectedArticle: Article | null;
  filter: 'all' | 'unread' | 'liked' | 'archive';
  // Inkwell-style fade bucket within the fresh window. null = show entire fresh window.
  bucket: 'today' | 'recent' | 'fading' | null;
  searchQuery: string;
  sharedArticles: Article[];
  currentContext: 'articles' | 'shared';
  setArticles: (articles: Article[]) => void;
  setSelectedArticle: (article: Article | null) => void;
  setFilter: (filter: 'all' | 'unread' | 'liked' | 'archive') => void;
  setBucket: (bucket: 'today' | 'recent' | 'fading' | null) => void;
  setSearchQuery: (query: string) => void;
  setSharedArticles: (articles: Article[]) => void;
  setCurrentContext: (context: 'articles' | 'shared') => void;
  updateArticle: (id: string, updates: Partial<Article>) => void;
}

export const useArticlesStore = create<ArticlesState>()(
  persist(
    (set) => ({
      articles: [],
      selectedArticle: null,
      // /read opens on Today: unread articles from the last day.
      filter: 'unread',
      bucket: 'today',
      searchQuery: '',
      sharedArticles: [],
      currentContext: 'articles',
      setArticles: (articles) => set({ articles }),
      setSelectedArticle: (article) => set({ selectedArticle: article }),
      setFilter: (filter) => set({ filter }),
      setBucket: (bucket) => set({ bucket }),
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSharedArticles: (articles) => set({ sharedArticles: articles }),
      setCurrentContext: (context) => set({ currentContext: context }),
      updateArticle: (id, updates) =>
        set((state) => ({
          articles: state.articles.map((article) =>
            article.id === id ? { ...article, ...updates } : article
          ),
          sharedArticles: state.sharedArticles.map((article) =>
            article.id === id ? { ...article, ...updates } : article
          ),
          selectedArticle:
            state.selectedArticle?.id === id
              ? { ...state.selectedArticle, ...updates }
              : state.selectedArticle,
        })),
    }),
    {
      name: 'ourss-articles',
      // Articles are always refetched; persisting them produced stale lists.
      // Version 1 drops any `articles` array left over from earlier builds.
      version: 1,
      migrate: (persisted) => {
        const state = (persisted ?? {}) as Partial<ArticlesState>;
        return {
          filter: state.filter ?? 'unread',
          bucket: state.bucket ?? 'today',
          searchQuery: state.searchQuery ?? '',
        };
      },
      partialize: (state) => ({
        filter: state.filter,
        bucket: state.bucket,
        searchQuery: state.searchQuery,
      }),
    }
  )
);

interface Feed {
  id: string;
  title: string;
  unreadCount?: number;
  feed: {
    id: string;
    url: string;
    title: string;
    description?: string;
    siteUrl?: string;
    imageUrl?: string;
  };
}

interface FeedsState {
  feeds: Feed[];
  selectedFeedId: string | null;
  selectedFolderId: string | null;
  setFeeds: (feeds: Feed[]) => void;
  setSelectedFeedId: (id: string | null) => void;
  setSelectedFolderId: (id: string | null) => void;
  decrementUnreadCount: (feedId: string, count?: number) => void;
}

export const useFeedsStore = create<FeedsState>()(
  persist(
    (set) => ({
      feeds: [],
      selectedFeedId: null,
      selectedFolderId: null,
      setFeeds: (feeds) => set({ feeds }),
      setSelectedFeedId: (id) => set({ selectedFeedId: id }),
      setSelectedFolderId: (id) => set({ selectedFolderId: id }),
      decrementUnreadCount: (feedId, count = 1) =>
        set((state) => ({
          feeds: state.feeds.map((feed) =>
            feed.feed.id === feedId
              ? { ...feed, unreadCount: Math.max(0, (feed.unreadCount ?? 0) - count) }
              : feed
          ),
        })),
    }),
    {
      name: 'ourss-feeds',
    }
  )
);


interface LikedArticlesState {
  likedArticleIds: Set<string>;
  addLikedArticle: (id: string) => void;
  removeLikedArticle: (id: string) => void;
  isArticleLiked: (id: string) => boolean;
}

export const useLikedArticlesStore = create<LikedArticlesState>()(
  persist(
    (set, get) => ({
      likedArticleIds: new Set(),
      addLikedArticle: (id) =>
        set((state) => ({
          likedArticleIds: new Set([...state.likedArticleIds, id]),
        })),
      removeLikedArticle: (id) =>
        set((state) => {
          const newSet = new Set(state.likedArticleIds);
          newSet.delete(id);
          return { likedArticleIds: newSet };
        }),
      isArticleLiked: (id) => get().likedArticleIds.has(id),
    }),
    {
      name: 'ourss-liked-articles',
      partialize: (state) => ({
        likedArticleIds: Array.from(state.likedArticleIds),
      }),
      onRehydrateStorage: () => (state) => {
        if (state && Array.isArray(state.likedArticleIds)) {
          state.likedArticleIds = new Set(state.likedArticleIds);
        }
      },
    }
  )
);

interface Notification {
  id: string;
  type: string;
  content: string;
  read: boolean;
  createdAt: string;
  fromUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
  };
  share?: {
    id: string;
    article: {
      id: string;
      title: string;
    };
  };
  comment?: {
    id: string;
    article: {
      id: string;
      title: string;
    };
  };
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  setNotifications: (notifications: Notification[]) => void;
  setUnreadCount: (count: number) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Notification) => void;
}

export const useNotificationsStore = create<NotificationsState>()(
  (set) => ({
    notifications: [],
    unreadCount: 0,
    setNotifications: (notifications) => set({ notifications }),
    setUnreadCount: (count) => set({ unreadCount: count }),
    markAsRead: (id) =>
      set((state) => ({
        notifications: state.notifications.map((notification) =>
          notification.id === id ? { ...notification, read: true } : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      })),
    markAllAsRead: () =>
      set((state) => ({
        notifications: state.notifications.map((notification) => ({
          ...notification,
          read: true,
        })),
        unreadCount: 0,
      })),
    addNotification: (notification) =>
      set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadCount: notification.read ? state.unreadCount : state.unreadCount + 1,
      })),
  })
);

interface Folder {
  id: string;
  userId: string;
  name: string;
  parentId?: string;
  createdAt: string;
  subscriptions: any[];
  children: Folder[];
  unreadCount?: number;
  _count: {
    subscriptions: number;
  };
}

interface FoldersState {
  folders: Folder[];
  setFolders: (folders: Folder[]) => void;
  addFolder: (folder: Folder) => void;
  updateFolder: (id: string, updates: Partial<Folder>) => void;
  removeFolder: (id: string) => void;
  decrementUnreadCount: (folderId: string, count?: number) => void;
}

export const useFoldersStore = create<FoldersState>()(
  (set) => ({
    folders: [],
    setFolders: (folders) => set({ folders }),
    addFolder: (folder) =>
      set((state) => ({
        folders: [...state.folders, folder],
      })),
    updateFolder: (id, updates) =>
      set((state) => ({
        folders: state.folders.map((folder) =>
          folder.id === id ? { ...folder, ...updates } : folder
        ),
      })),
    removeFolder: (id) =>
      set((state) => ({
        folders: state.folders.filter((folder) => folder.id !== id),
      })),
    decrementUnreadCount: (folderId, count = 1) =>
      set((state) => ({
        folders: state.folders.map((folder) =>
          folder.id === folderId
            ? { ...folder, unreadCount: Math.max(0, (folder.unreadCount ?? 0) - count) }
            : folder
        ),
      })),
  })
);
